import { useEffect, useState } from 'react';
import { useIsHydrating } from '@/hooks/useHydration';
import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import AboutSection from '@/components/AboutSection';
import ServicesSection from '@/components/ServicesSection';
import ProcessSection from '@/components/ProcessSection';
import EmergencyCTA from '@/components/EmergencyCTA';
import BackupPowerSection from '@/components/BackupPowerSection';
import TestimonialsSection from '@/components/TestimonialsSection';
import ReviewsSection from '@/components/ReviewsSection';
import YelpBadge from '@/components/YelpBadge';
import GallerySection from '@/components/GallerySection';
import BlogSection from '@/components/BlogSection';
import FAQSection from '@/components/FAQSection';
import ServiceAreaSection from '@/components/ServiceAreaSection';
import ContactSection from '@/components/ContactSection';
import Footer from '@/components/Footer';

const STRUCTURED_DATA = [
  {
    "@context": "https://schema.org",
    "@type": "Electrician",
    "name": "Evolution Electric",
    "telephone": "(615) 961-5930",
    "email": "support@nashvilleelectricalservice.com",
    "url": "https://nashvilleelectricalservice.com",
    "logo": "https://nashvilleelectricalservice.com/logo.jpg",
    "image": "https://nashvilleelectricalservice.com/logo.jpg",
    "description": "Licensed IBEW electricians serving Nashville and surrounding counties since 2014. Residential, commercial, and industrial electrical services including panel upgrades, EV charger installation, generators, and after-hours emergency service.",
    "foundingDate": "2014",
    "knowsAbout": ["residential electrical services", "commercial electrical services", "EV charger installation", "electrical panel upgrades", "whole-home rewiring", "generator installation", "electrical safety inspections", "smart home wiring", "surge protection", "emergency electrical repair"],
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Nashville",
      "addressRegion": "TN",
      "addressCountry": "US",
      "postalCode": "37201"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "36.1627",
      "longitude": "-86.7816"
    },
    "areaServed": [
      { "@type": "City", "name": "Nashville", "sameAs": "https://en.wikipedia.org/wiki/Nashville,_Tennessee" },
      { "@type": "AdministrativeArea", "name": "Davidson County, TN" },
      { "@type": "AdministrativeArea", "name": "Williamson County, TN" },
      { "@type": "AdministrativeArea", "name": "Rutherford County, TN" },
      { "@type": "AdministrativeArea", "name": "Wilson County, TN" },
      { "@type": "AdministrativeArea", "name": "Sumner County, TN" },
      { "@type": "AdministrativeArea", "name": "Robertson County, TN" },
      { "@type": "AdministrativeArea", "name": "Cheatham County, TN" },
      { "@type": "AdministrativeArea", "name": "Dickson County, TN" },
      { "@type": "AdministrativeArea", "name": "Maury County, TN" }
    ],
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "08:00", "closes": "16:30" }
    ],
    "priceRange": "$$",
    "paymentAccepted": "Cash, Credit Card, Check",
    "currenciesAccepted": "USD",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Electrical Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrical Troubleshooting & Repairs", "description": "Fast diagnosis and expert resolution of residential and commercial electrical issues in Nashville." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrical Panel Upgrades", "description": "Electrical panel upgrades and replacements based on your home's existing system, electrical demand, and project requirements." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EV Charger Installation", "description": "Professional home EV charger installation with electrical-capacity assessment and dedicated-circuit planning." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Generator & Backup Power", "description": "Whole-home standby generators and battery backup systems for Nashville homes." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Lighting & Switch Installation", "description": "Recessed lighting, ceiling fans, smart switches, and outlet installations in Nashville." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Whole-Home Surge Protection", "description": "Professional surge protector installation for Nashville homes. Protect electronics, HVAC, and appliances from power surges." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Electrical Safety Inspections", "description": "Comprehensive home electrical inspections for real estate, insurance, and safety." } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pool & Spa Wiring", "description": "Code-compliant GFCI pool and spa electrical wiring in Nashville." } }
      ]
    },
    "sameAs": []
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "How much does an electrician cost in Nashville TN?", "acceptedAnswer": { "@type": "Answer", "text": "Residential electrical service pricing depends on the nature of the work, project scope, and site conditions. Evolution Electric provides project-based pricing after evaluating the work needed. Call (615) 961-5930 for a consultation." } },
      { "@type": "Question", "name": "How quickly can you respond to an electrical emergency in Nashville?", "acceptedAnswer": { "@type": "Answer", "text": "Call Evolution Electric at (615) 961-5930 to discuss your electrical issue. We serve Nashville and Middle Tennessee homeowners during regular hours Monday–Friday 8:00 AM – 4:30 PM, with weekend service by appointment." } },
      { "@type": "Question", "name": "Are your Nashville electricians licensed and insured?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. All Evolution Electric electricians are licensed by the State of Tennessee with IBEW Journeyman Experience. We carry comprehensive liability insurance and workers' compensation coverage." } },
      { "@type": "Question", "name": "What areas does Evolution Electric serve in Tennessee?", "acceptedAnswer": { "@type": "Answer", "text": "We serve Nashville and all surrounding counties including Davidson, Williamson, Rutherford, Wilson, and Sumner counties. This includes Brentwood, Franklin, Murfreesboro, Hendersonville, Mount Juliet, Lebanon, Gallatin, Spring Hill, and Smyrna." } },
      { "@type": "Question", "name": "How much does an electrical panel upgrade cost in Nashville?", "acceptedAnswer": { "@type": "Answer", "text": "Electrical panel upgrade pricing depends on the existing electrical system, project scope, equipment, service capacity, and site conditions. Evolution Electric provides detailed project-based pricing after evaluating the property. Call (615) 961-5930." } },
      { "@type": "Question", "name": "How much does EV charger installation cost in Nashville?", "acceptedAnswer": { "@type": "Answer", "text": "EV charger installation pricing depends on your existing electrical panel, available capacity, the distance from the panel to the charger location, and the scope of work required. Evolution Electric provides project-based pricing after evaluating the property." } },
      { "@type": "Question", "name": "Do I need a permit for electrical work in Nashville?", "acceptedAnswer": { "@type": "Answer", "text": "Permitting and inspection requirements vary by project scope and local jurisdiction. Evolution Electric can explain the requirements that apply to your project and handle permitting where applicable." } },
      { "@type": "Question", "name": "What is the best electrician in Nashville TN?", "acceptedAnswer": { "@type": "Answer", "text": "Evolution Electric maintains a 5.0-star Google rating. Licensed by the State of Tennessee with IBEW Journeyman Experience, we have been serving Nashville homeowners and businesses since 2014. We provide upfront project-based pricing. Call (615) 961-5930." } }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Hire a Licensed Electrician in Nashville, TN",
    "description": "Steps to find and hire a reliable, licensed electrician in Nashville for your home or business electrical needs.",
    "step": [
      { "@type": "HowToStep", "name": "Verify Licensing", "text": "Ensure the electrician is licensed by the State of Tennessee. Evolution Electric holds full Tennessee licensing with IBEW Journeyman Experience." },
      { "@type": "HowToStep", "name": "Check Insurance", "text": "Confirm the electrician carries liability insurance and workers' compensation. This protects you from liability during the project." },
      { "@type": "HowToStep", "name": "Get a Written Quote", "text": "Request a detailed, written estimate before work begins. Evolution Electric provides project-based pricing with no surprise fees." },
      { "@type": "HowToStep", "name": "Verify Permits", "text": "Most Nashville electrical work requires permits. Your electrician should handle all permitting and inspection coordination." },
      { "@type": "HowToStep", "name": "Schedule the Work", "text": "Book your appointment online at nashvilleelectricalservice.com/book or call (615) 961-5930. Regular hours: Mon–Fri 8:00 AM – 4:30 PM. Weekends by appointment. After-hours emergency service available." }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Evolution Electric",
    "url": "https://nashvilleelectricalservice.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://nashvilleelectricalservice.com/?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Evolution Electric Service Pages",
    "itemListElement": [
      { "@type": "SiteNavigationElement", "position": 1, "name": "Electrical Panel Upgrades Nashville", "url": "https://nashvilleelectricalservice.com/services/electrical-panel-upgrade-nashville" },
      { "@type": "SiteNavigationElement", "position": 2, "name": "EV Charger Installation Nashville", "url": "https://nashvilleelectricalservice.com/services/ev-charger-installation-nashville" },
      { "@type": "SiteNavigationElement", "position": 3, "name": "Electrical Repair Nashville", "url": "https://nashvilleelectricalservice.com/services/electrical-repair-nashville" },
      { "@type": "SiteNavigationElement", "position": 4, "name": "Lighting Installation Nashville", "url": "https://nashvilleelectricalservice.com/services/lighting-installation-nashville" },
      { "@type": "SiteNavigationElement", "position": 5, "name": "Surge Protection Nashville", "url": "https://nashvilleelectricalservice.com/services/surge-protection-nashville" },
      { "@type": "SiteNavigationElement", "position": 6, "name": "Electrical Inspection Nashville", "url": "https://nashvilleelectricalservice.com/services/electrical-inspection-nashville" },
      { "@type": "SiteNavigationElement", "position": 7, "name": "Pool & Spa Wiring Nashville", "url": "https://nashvilleelectricalservice.com/services/pool-spa-wiring-nashville" },
      { "@type": "SiteNavigationElement", "position": 8, "name": "Residential Electrical Services Nashville", "url": "https://nashvilleelectricalservice.com/services/residential-electrical-services-nashville" },
      { "@type": "SiteNavigationElement", "position": 9, "name": "Commercial Electrical Services Nashville", "url": "https://nashvilleelectricalservice.com/services/commercial-electrical-services-nashville" }
    ]
  }
];

function useStructuredData() {
  useEffect(() => {
    const scripts = STRUCTURED_DATA.map(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      document.head.appendChild(script);
      return script;
    });
    return () => scripts.forEach(s => document.head.removeChild(s));
  }, []);
}

export default function HomePage() {
  useStructuredData();

  // During hydration/prerender: only navbar+hero render (matches prerendered HTML).
  // After hydration: isHydrating becomes false → below-fold mounts.
  // On SPA navigation (non-hydration): everything renders immediately.
  const isHydrating = useIsHydrating();
  const [showBelowFold, setShowBelowFold] = useState(!isHydrating);

  useEffect(() => {
    if (!showBelowFold && !isHydrating) setShowBelowFold(true);
  }, [showBelowFold, isHydrating]);

  return (
    <div data-testid="home-page">
      <Navbar />
      <main>
        <HeroSection />
        {showBelowFold && (
          <>
            <AboutSection />
            <ServicesSection />
            <ProcessSection />
            <EmergencyCTA />
            <BackupPowerSection />
            <TestimonialsSection />
            <ReviewsSection />
            <YelpBadge />
            <GallerySection />
            <BlogSection />
            <FAQSection />
            <ServiceAreaSection />
            <ContactSection />
          </>
        )}
      </main>
      {showBelowFold && <Footer />}
    </div>
  );
}
